import React, { useState, useEffect, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import AddNew from 'assets/icons/add-new'
import EditIcon from 'assets/icons/edit-icon'
import ViewIcon from 'assets/icons/view-site-icon'
import TrashBox from 'assets/icons/trash-box'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Button from 'components/Button'
import Pagination from 'features/Pagination'
import Loader from 'components/Loader'
import { TabsWrapper, ValidationDiv } from './style'
import SpeciesStateForm from './SpeciesStateForm'
import AddStateEvaluationModal from './AddStateEvaluationModal'
import { updateTriggerStateBenchmark } from 'api/monitoring'
import RemoveStateModal from './RemoveStateModal'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import CalendarIcon from 'assets/icons/calendar-icon'
import Modal from 'components/Modal'
import { MonitoringContext } from 'providers/monitoring'
import {
  CongregationHeaderCalendarDivider,
  CongregationHeaderCalendarIconWrapper,
  CongregationHeaderCalendarDateText
} from 'flow/SiteProposal/pages/Assessment/components/CongregationsSection/style'
import { BiodiversitySelectorWrapper, ItemWrapper, PaginationWrapper, Tab } from 'components/BiodiversitySelector/style'
import ErrorIcon from 'assets/icons/error-input-trailing-icon'
import CheckboxArrowIcon from 'assets/icons/checkbox-left-arrow'
import AddIcon from 'assets/icons/add-new'
import { ViewButton, ButtonContent, TextSlot } from 'components/BiodiversitySelector/style'
import CircleIcon from 'assets/icons/circle-icon'
import ProgressComplete from 'assets/icons/progress-complete'

const StateEvaluationSelector = ({
  onBack,
  onEvaluationCreated: onEvalCreatedProp,
  onRemoveSelectedItem,
  siteStateId,
  incompleteBenchmarkIds,
  isSiteStateValid,
  siteId,
  isEditable
}) => {
  const { biodiversityFilter, setBiodiversityFilter } = useContext(MonitoringContext)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [list, setList] = useState([])
  const [evaluationId, setEvaluationId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [benchmarksPagination, setBenchmarksPagination] = useState({
    resultsPerPage: 12,
    totalNumber: 0,
    totalPages: 1,
    currentPage: 1,
    hasNext: false,
    hasPrevious: false
  })
  const [dataFilter, setDataFilter] = useState(biodiversityFilter)
  const [stateForDelete, setStateForDelete] = useState({})

  const fetchBenchmarks = async (
    page = benchmarksPagination?.currentPage ?? 1,
    limit = benchmarksPagination?.resultsPerPage ?? 12,
    filter = biodiversityFilter
  ) => {
    setLoading(true)
    if (!siteStateId) {
      setLoading(false)
      return
    }
    try {
      const url = `/api/trigger-state-benchmark/${siteStateId}/site-state?page=${page}&limit=${limit}&filter=${filter}&site_id=${siteId}`
      const res = await fetch(url)
      const data = await res.json()
      const rows = Array.isArray(data?.rows) ? data.rows : []
      const paginationInfo = data?.pagination || data?.paginationInfo || null

      let normalized = []

      if (filter === 1) {
        normalized = rows.map((group, groupIndex) => ({
          id: `group-${groupIndex}`,
          taxonomicGroup: group.taxonomicGroup || 'Unknown group',
          species: Array.isArray(group.species)
            ? group.species.map((sp) => ({
                id: sp.id,
                name: sp.scientificName || sp.name || '',
                species: sp.specieName,
                evaluationId: sp?.evaluations?.[0]?.id || null,
                isAdded: sp?.is_added === true,
                raw: sp
              }))
            : []
        }))
      } else {
        normalized = rows.map((eco) => ({
          id: eco.id,
          name: eco.name,
          evaluationId: eco?.evaluations?.[0]?.id || null,
          isAdded: eco?.is_added === true,
          raw: eco
        }))
      }

      setList(normalized)
      setDataFilter(filter)

      if (paginationInfo) setBenchmarksPagination(paginationInfo)
      else
        setBenchmarksPagination({
          totalNumber: paginationInfo.totalNumber,
          resultsPerPage: paginationInfo.resultsPerPage,
          totalPages: paginationInfo.totalPages,
          currentPage: paginationInfo.currentPage,
          hasNext: paginationInfo.currentPage < paginationInfo.totalPages,
          hasPrevious: paginationInfo.currentPage > 1
        })
    } catch (e) {
      console.error(e)
      setList([])
      setBenchmarksPagination(null)
    } finally {
      setLoading(false)
    }
  }
  const filteredItems = list

  useEffect(() => {
    if (siteStateId) {
      fetchBenchmarks(1, benchmarksPagination?.resultsPerPage, biodiversityFilter)
    }
  }, [biodiversityFilter, siteStateId])

  const handleBenchNext = () => {
    if (!benchmarksPagination?.hasNext) return
    fetchBenchmarks(benchmarksPagination.currentPage + 1, benchmarksPagination.resultsPerPage, biodiversityFilter)
  }

  const handleBenchPrev = () => {
    if (!benchmarksPagination?.hasPrevious) return
    fetchBenchmarks(benchmarksPagination.currentPage - 1, benchmarksPagination.resultsPerPage, biodiversityFilter)
  }

  const handleBenchSelectPage = (page) => {
    fetchBenchmarks(page, benchmarksPagination.resultsPerPage, biodiversityFilter)
  }

  const handleBenchPerPageChange = (newPerPage) => {
    const perPage = Number(newPerPage?.value) || 12
    fetchBenchmarks(1, perPage, biodiversityFilter)
  }

  const handleEvaluationIdUpdate = (id) => {
    setEvaluationId(id)
    if (!selectedItem) return
    const benchmarkId = selectedItem.benchmarkId || selectedItem.id
    setList((prev) =>
      prev.map((it) =>
        it.id === selectedItem.id
          ? {
              ...it,
              evaluationId: id,
              isAdded: true
            }
          : it
      )
    )
    if (benchmarkId) {
      updateTriggerStateBenchmark(benchmarkId, { is_added: true }).catch((err) =>
        console.error('Failed to mark benchmark as added:', err)
      )
    }
    fetchBenchmarks()
    onEvalCreatedProp?.(id)
  }

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => {
    setIsModalOpen(false)

    fetchBenchmarks(
      benchmarksPagination?.currentPage ?? 1,
      benchmarksPagination?.resultsPerPage ?? 12,
      biodiversityFilter
    )
    setSelectedItem(null)
  }

  const handleTypeChange = (selectedOption) => {
    const value = typeof selectedOption === 'number' ? selectedOption : (selectedOption?.value ?? 1)
    setBiodiversityFilter(value)
  }

  const handleDelete = async (item) => {
    const rawBench = item?.raw?.triggerStateBenchmark ?? item?.raw?.trigger_state_benchmark ?? item?.raw?.benchmark
    let deleteId = null
    if (Array.isArray(rawBench)) deleteId = rawBench[0]?.id
    else if (rawBench && typeof rawBench === 'object') deleteId = rawBench.id
    if (!deleteId) deleteId = item?.benchmarkId ?? item?.raw?.id ?? null
    const evaluationId = item.evaluationId
    if (!deleteId) {
      console.error('handleDelete: could not find deleteId for item:', item)
      showErrorToast('Could not delete item — no ID found')
      return
    }
    try {
      if (evaluationId) {
        await fetch(`/api/trigger-state-evaluation/${evaluationId}`, {
          method: 'DELETE'
        })
      }
      const response = await fetch(`/api/trigger-state-benchmark/${deleteId}`, {
        method: 'DELETE'
      })
      //  setIsSelectedAll(false)
      const originalItemId = item?.raw?.siteSpeciesId || item?.raw?.siteEcosystemId || null
      onRemoveSelectedItem?.(originalItemId)

      const getBenchmarkId = (item) => {
        const raw = item?.raw?.triggerStateBenchmark ?? item?.raw?.trigger_state_benchmark ?? item?.raw?.benchmark
        if (Array.isArray(raw)) return raw[0]?.id
        if (raw && typeof raw === 'object') return raw.id
        return item?.benchmarkId ?? item?.raw?.id ?? null
      }
      setList((prev) => prev.filter((i) => getBenchmarkId(i) !== deleteId))
      showSuccessToast('Biodiversity element deleted successfully')
    } catch (error) {
      console.error(error)
      showErrorToast('Failed to delete evaluation. Please try again.')
    } finally {
      const resultsPerPage = benchmarksPagination?.resultsPerPage ?? 12
      const totalNumber = Math.max(0, (benchmarksPagination?.totalNumber ?? 0) - 1)
      const totalPages = Math.max(1, Math.ceil(totalNumber / resultsPerPage))
      const currentPage = benchmarksPagination?.currentPage ?? 1
      const pageToFetch = Math.min(currentPage, totalPages)
      fetchBenchmarks(pageToFetch, resultsPerPage, biodiversityFilter)
    }
  }

  const renderItem = (item) => {
    const isEvaluated = item.evaluationId || item.isAdded === true
    const isAdded = item.isAdded === true
    const hasEvaluationId = !!item.evaluationId
    const showEdit = isAdded || hasEvaluationId
    const evaluationIdToSend = item.evaluationId || null
    return (
      <ItemWrapper key={item.id} style={{ marginLeft: '20px' }} showBorderTop={false}>
        <div className="item-left">
          <div className="item-info">
            {biodiversityFilter === 1 && (
              <span style={{ marginBottom: '5px' }}>
                <CheckboxArrowIcon />
              </span>
            )}
            <span>{isEvaluated ? <ProgressComplete /> : <CircleIcon />}</span>
            <div className="item-name">
              <span>
                <i>{item.name}</i>
              </span>
              {item.species && <span> {item.species}</span>}
              {item.raw.season && (
                <span>{`(${item.raw.season.charAt(0).toUpperCase() + item.raw.season.slice(1)})`}</span>
              )}
            </div>
          </div>
        </div>
        <div className="item-right">
          <div className="calendar-section">
            {isEvaluated && (
              <>
                <CongregationHeaderCalendarDivider />
                <CongregationHeaderCalendarIconWrapper>
                  <CalendarIcon />
                </CongregationHeaderCalendarIconWrapper>
                <CongregationHeaderCalendarDateText>
                  {item?.raw?.created_at &&
                    new Date(item.raw.created_at)
                      .toLocaleString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })
                      .replace(',', '')}
                </CongregationHeaderCalendarDateText>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {showEdit ? (
              <Button
                height="34px"
                marginTop="0"
                withIcon
                width="auto"
                onClick={() => {
                  setSelectedItem(item)
                  setEvaluationId(evaluationIdToSend)
                  openModal()
                }}
              >
                {!isEditable ? (
                  <>
                    <ViewIcon /> &nbsp; <FormattedMessage id="view" />
                  </>
                ) : (
                  <>
                    <EditIcon /> &nbsp; <FormattedMessage id="edit" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                height="34px"
                marginTop="0"
                type="primary"
                withIcon
                onClick={() => {
                  setSelectedItem(item)
                  setEvaluationId(evaluationIdToSend)
                  openModal()
                }}
              >
                <AddNew /> &nbsp; <FormattedMessage id="evaluate" />
              </Button>
            )}
            <Button
              type="disalbeBorder"
              isDisabled={!isEditable}
              height="34px"
              marginTop="0"
              border={0}
              onClick={() => setStateForDelete(item)}
            >
              <TrashBox />
            </Button>
          </div>
        </div>
        {incompleteBenchmarkIds && incompleteBenchmarkIds?.includes(item?.id) && (
          <ValidationDiv>
            <ErrorIcon />
          </ValidationDiv>
        )}
      </ItemWrapper>
    )
  }

  return (
    <Container>
      <Row>
        <Col md={8} mdOffset={2}>
          <BiodiversitySelectorWrapper style={{ marginTop: '60px' }}>
            {loading || biodiversityFilter !== dataFilter ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '300px'
                }}
              >
                <Loader />
              </div>
            ) : (
              <>
                <TabsWrapper showBorder={filteredItems.length === 0}>
                  <Tab className={biodiversityFilter === 1 ? 'active' : ''} onClick={() => handleTypeChange(1)}>
                    <FormattedMessage id="species" />
                    {biodiversityFilter === 1 && <> {`(${benchmarksPagination?.totalNumber || 0})`}</>}
                  </Tab>
                  <Tab className={biodiversityFilter === 2 ? 'active' : ''} onClick={() => handleTypeChange(2)}>
                    <FormattedMessage id="ecosystem" />
                    {biodiversityFilter === 2 && <> {`(${benchmarksPagination?.totalNumber || 0})`}</>}
                  </Tab>
                </TabsWrapper>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                  <ViewButton
                    style={{
                      fontWeight: '500',
                      fontSize: '14px',
                      padding: '12px',
                      width: 'auto',
                      marginBottom: '10px',
                      background: '#0AA1F4',
                      color: 'white',
                      border: 'none'
                    }}
                    onClick={() => {
                      if (isEditable) onBack?.()
                    }}
                    disabled={!isEditable}
                  >
                    <ButtonContent>
                      <AddIcon />
                      <TextSlot>
                        {biodiversityFilter === 1 ? (
                          <FormattedMessage id="addMoreSpecies" />
                        ) : (
                          <FormattedMessage id="addMoreEcosystems" />
                        )}
                      </TextSlot>
                    </ButtonContent>
                  </ViewButton>
                </div>
                {filteredItems.length === 0 ? (
                  <div className="no-records-text" style={{ textAlign: 'center', padding: '32px', color: '#333333' }}>
                    <FormattedMessage id="no.records.found" defaultMessage="No records found" />
                  </div>
                ) : (
                  <>
                    {biodiversityFilter === 1 &&
                      filteredItems.map((group) => (
                        <div key={group.id}>
                          <div
                            className="tree-group-header"
                            style={{ fontWeight: '500', color: '#333333', marginTop: '20px' }}
                          >
                            {group.taxonomicGroup}
                          </div>
                          {group.species.map((item) => renderItem(item))}
                        </div>
                      ))}

                    {biodiversityFilter === 2 && filteredItems.map((item) => renderItem(item))}
                  </>
                )}

                {filteredItems.length > 0 && (
                  <PaginationWrapper>
                    <ItemsPerPageDropdown
                      currentPage={benchmarksPagination?.currentPage}
                      resultsPerPage={benchmarksPagination?.resultsPerPage}
                      totalNumber={benchmarksPagination?.totalNumber}
                      onChange={benchmarksPagination && handleBenchPerPageChange}
                    />
                    {benchmarksPagination?.totalPages > 1 && (
                      <Pagination
                        activePages={benchmarksPagination?.currentPage}
                        numberOfPages={benchmarksPagination?.totalPages}
                        nextPage={benchmarksPagination && handleBenchNext}
                        prevPage={benchmarksPagination && handleBenchPrev}
                        selectPage={benchmarksPagination && handleBenchSelectPage}
                      />
                    )}
                  </PaginationWrapper>
                )}
              </>
            )}
          </BiodiversitySelectorWrapper>
        </Col>
      </Row>

      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 50,
            left: 0,
            width: '100%',
            display: 'flex',
            minHeight: '90vh',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            background: '#fff'
          }}
        >
          <AddStateEvaluationModal autoSaveIcon={true} onClose={closeModal}>
            <SpeciesStateForm
              triggerItem={selectedItem}
              onCancel={closeModal}
              evaluationId={evaluationId}
              onEvaluationCreated={handleEvaluationIdUpdate}
              isSiteStateValid={isSiteStateValid}
              isEditable={isEditable}
            />
          </AddStateEvaluationModal>
        </div>
      )}

      <Modal
        width="416px"
        isOpen={Boolean(stateForDelete.id)}
        toggleModal={() => setStateForDelete({})}
        modalTitleId="Removestate"
        bottomBorder={true}
      >
        <RemoveStateModal
          item={stateForDelete}
          closeModal={() => setStateForDelete({})}
          confirmRemove={() => {
            handleDelete(stateForDelete)
            setStateForDelete({})
          }}
        />
      </Modal>
    </Container>
  )
}

export default StateEvaluationSelector
